/** @file Turns a thrown value into UI text: shell codes translated, others generic, hint on its own line. */
import { toPose2DError } from '@pose2d/format/toPose2DError';
import type { ErrorCode, Pose2DError } from '@pose2d/format/types';
import type { CatalogKey, Translate } from '@pose2d/i18n/types';

const KEYS: Partial<Record<ErrorCode, CatalogKey>> = {
  UNAUTHORIZED: 'errors.unauthorized',
  RATE_LIMITED: 'errors.rateLimited',
  NAME_TAKEN: 'errors.nameTaken',
  INVALID_NAME: 'errors.invalidName',
  NOT_FOUND: 'errors.notFound',
};

/** The client reports transport failures as NOT_FOUND for a `route`; "not found" would hide the real cause. */
function isTransportFailure(error: Pose2DError): boolean {
  return error.code === 'NOT_FOUND' && error.params?.kind === 'route';
}

/** The UI translates by code (spec §4.2); other codes show the English server message; the hint follows after a newline. */
export function errorText(t: Translate, thrown: unknown): string {
  const error = toPose2DError(thrown);
  if (error === null) return t('errors.generic', { message: thrown instanceof Error ? thrown.message : String(thrown) });
  const key = isTransportFailure(error) ? undefined : KEYS[error.code];
  const text = key === undefined ? t('errors.generic', { message: error.message }) : t(key, error.params);
  return error.hint === undefined ? text : `${text}\n${error.hint}`;
}
